import React, { useEffect, useMemo, useState } from "react";
import { FC } from "react";
import { Availability } from "../../../../models";
import moment from "moment";
import Typography from "../../../../components/Typography";
import { TK } from "../../../../store/translations/translationKeys";
import { useTranslations } from "../../../../store/translations/hooks";
import AppButton from '../../../../components/AppButton';

const dayStyle = (disabled: boolean, selected: boolean, borderless: boolean = false): React.CSSProperties => ({
    padding: 'var(--spacer-S)',
    textAlign: 'center',
    borderStyle: 'solid',
    borderRadius: '5px',
    borderColor: selected ? 'var(--green)' : 'lightgray',
    borderWidth: borderless ? '0px' : '2px',
    background: disabled ? 'lightgray' : selected ? 'var(--green)' : undefined,
    opacity: disabled ? 0.5 : undefined,
    color: selected ? 'white' : undefined,
    cursor: disabled || borderless ? undefined : 'pointer',
})

export const DateTimeSelector: FC<{
    options: Availability[]
    value: string | undefined
    onUpdate: (value: string | undefined) => void
}> = ({
    options,
    value,
    onUpdate,
}) => {
    const t = useTranslations()
    const [selectedMonth, setSelectedMonth] = useState<string>(moment().format('YYYY-MM'))
    const [selectedDay, setSelectedDay] = useState<number|undefined>()

    useEffect(() => {
        if (!value) {
            setSelectedDay(undefined)
        }
    }, [value])

    useEffect(() => {
        setSelectedDay(undefined)
        onUpdate(undefined)
    }, [selectedMonth])

    useEffect(() => {
        onUpdate(undefined)
    }, [selectedDay])

    const selectableDays = useMemo(
        () => new Set(options.filter(({dateTime}) => dateTime.startsWith(selectedMonth)).map(({dateTime}) => Number(dateTime.substring(8, 10)))),
        [selectedMonth, options],
    )

    const timeSlots = useMemo(() => {
        if (!selectedDay) return []
        const date = `${selectedMonth}-${String(selectedDay).padStart(2, '0')}`
        const optionsInDate = options.filter(a => a.dateTime.startsWith(date))
        const slots = [...new Set(optionsInDate.map(({dateTime}) => dateTime.substring(11, 16)))]
        return slots.sort((a, b) => a > b ? 1 : -1)
    }, [selectedDay])


    return (<>
        <div style={{display: 'flex', gap: 'var(--spacer-M)', width: '100%', justifyContent: 'center', fontSize: 'large', fontWeight: 'bold', alignItems: 'end'}}>
            <AppButton
                color="outline"
                style={{opacity: selectedMonth === moment().format('YYYY-MM') ? 0.2 : undefined}}
                onClick={() => setSelectedMonth(moment(selectedMonth).add(-1, 'M').format('YYYY-MM'))}
                disabled={selectedMonth === moment().format('YYYY-MM')}
            >
                <b>{'<'}</b>
            </AppButton>

            <div style={{minWidth: '8rem', textAlign: 'center'}}>{moment(selectedMonth).format('MMMM')}</div>

            <AppButton
                color="outline"
                onClick={() => setSelectedMonth(moment(selectedMonth).add(1, 'M').format('YYYY-MM'))}
            >
                <b>{'>'}</b>
            </AppButton>
        </div>
        <div style={{display: 'grid', gap: 'var(--spacer-XS)', gridTemplateColumns: 'repeat(7, 1fr)'}}>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[0]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[1]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[2]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[3]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[4]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[5]}</div>
            <div style={dayStyle(false, false, true)}>{moment.weekdaysShort()[6]}</div>
            {Array.from(Array(moment(selectedMonth).day())).map((_, i) =>
                <div key={`${selectedMonth}-${i}-empty`} style={dayStyle(false, false, true)}/>
            )}
            {Array.from(Array(moment(selectedMonth).daysInMonth()).keys()).map(d => d+1).map(day => (
                <div
                    key={`${selectedMonth}-${day}`}
                    style={dayStyle(!selectableDays.has(day), selectedDay === day)}
                    onClick={() => selectableDays.has(day) && setSelectedDay(day)}
                >
                    {day}
                </div>
            ))}
            {Array.from(Array(6 - moment(selectedMonth).add(1, 'M').add(-1, 'd').day())).map((_, i) =>
                <div key={`${selectedMonth}-${i}-end`} style={dayStyle(false, false, true)}/>
            )}
        </div>

        {!!selectedDay && <>
            <Typography>{t(TK.time)}</Typography>
            <div style={{display: 'grid', gap: 'var(--spacer-XS)', gridTemplateColumns: 'repeat(6, 1fr)'}}>
                {timeSlots.map(timeSlot => (
                    <div
                        key={timeSlot}
                        style={dayStyle(false, value?.substring(11, 16) === timeSlot, false)}
                        onClick={() => onUpdate(`${selectedMonth}-${String(selectedDay).padStart(2, '0')}T${timeSlot}:00.000Z`)}
                    >
                        {timeSlot}
                    </div>
                ))}
            </div>
        </>}
    </>)
}
